
import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import { PDiagramData, ApprovalData } from '../types';
import { Target, SlidersHorizontal, LogIn, Settings, CheckCircle2, AlertTriangle, Waves, Check, X, XCircle, Sparkles } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import PDiagramGenerator from '../components/PDiagramGenerator';

const PDiagramBox: React.FC<{ title: string; items: string[]; icon: React.ReactNode; className?: string; contentClassName?: string }> = ({ title, items, icon, className = '', contentClassName = '' }) => {
    return (
        <div className={`bg-background/50 border border-border/50 rounded-lg p-3 flex flex-col h-full shadow-inner ${className}`}>
            <h3 className="font-bold text-center text-primary mb-2 text-base flex items-center justify-center gap-2">
                {icon}
                {title}
            </h3>
            <div className={`flex-grow overflow-y-auto ${contentClassName}`}>
                <ul className="list-disc list-inside text-sm space-y-1 text-text-secondary">
                    {items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const NoiseGrid: React.FC<{ noiseFactors: PDiagramData['noiseFactors'] }> = ({ noiseFactors }) => {
    const items = [
        { title: 'Piece to Piece', factors: noiseFactors.pieceToPiece },
        { title: 'Change Over Time', factors: noiseFactors.changeOverTime },
        { title: 'Customer Usage', factors: noiseFactors.customerUsage },
        { title: 'External Environment', factors: noiseFactors.externalEnvironment },
        { title: 'Internal', factors: noiseFactors.internal },
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {items.map(item => (
                <div key={item.title}>
                    <h4 className="font-semibold text-primary/80 mb-2 text-center text-sm">{item.title}</h4>
                    <ul className="list-disc list-inside text-xs space-y-1 text-text-secondary">
                        {item.factors.map(factor => <li key={factor}>{factor}</li>)}
                    </ul>
                </div>
            ))}
        </div>
    );
};

interface CreatePDiagramModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentDiagrams: PDiagramData[];
    onCreate: (newData: Partial<PDiagramData>) => void;
    masterFunctions: string[];
}

const CreatePDiagramModal: React.FC<CreatePDiagramModalProps> = ({ isOpen, onClose, currentDiagrams, onCreate, masterFunctions }) => {
    // Only show functions that don't have a DRAFT yet. It's okay if they have an active one.
    const availableFunctions = masterFunctions.filter(fn => !currentDiagrams.some(d => d.consolidatedFunctionId === fn && d.status === 'draft'));

    const [selectedFunction, setSelectedFunction] = useState(availableFunctions[0] || '');
    const [newDiagram, setNewDiagram] = useState<Partial<PDiagramData>>({
        inputSignals: [''],
        controlFactors: [''],
        noiseFactors: { pieceToPiece: [], changeOverTime: [], customerUsage: [], externalEnvironment: [], internal: [] },
        systemFunction: [''],
        expectedOutput: [''],
        unexpectedOutput: [''],
        goal: ['']
    });

    // Update selected function if available functions change
    React.useEffect(() => {
        if (availableFunctions.length > 0) {
            // Only reset if currently selected is no longer available, or if empty
            if (!selectedFunction || !availableFunctions.includes(selectedFunction)) {
                setSelectedFunction(availableFunctions[0]);
            }
        }
    }, [availableFunctions]);

    const handleNoiseFactorChange = (category: keyof PDiagramData['noiseFactors'], value: string) => {
        setNewDiagram(prev => ({
            ...prev,
            noiseFactors: {
                ...prev.noiseFactors!,
                [category]: value.split(',').map(s => s.trim()).filter(Boolean)
            }
        }));
    };

    const handleArrayChange = (field: keyof Omit<PDiagramData, 'noiseFactors' | 'id' | 'consolidatedFunctionId' | 'status' | 'approval'>, value: string) => {
        setNewDiagram(prev => ({
            ...prev,
            [field]: value.split(',').map(s => s.trim()).filter(Boolean)
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFunction) return;
        onCreate({ ...newDiagram, consolidatedFunctionId: selectedFunction });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Create New P-Diagram Draft</h2>
                {availableFunctions.length === 0 ? (
                    <div className="text-center py-4">
                        <p>All Master Functions already have a draft P-Diagram.</p>
                        <button onClick={onClose} className="mt-4 bg-primary text-white px-4 py-2 rounded">Close</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Select Function</label>
                            <select
                                value={selectedFunction}
                                onChange={(e) => setSelectedFunction(e.target.value)}
                                className="w-full bg-background border border-border rounded p-2"
                            >
                                {availableFunctions.map(fn => <option key={fn} value={fn}>{fn}</option>)}
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Goal (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('goal', e.target.value)} placeholder="Maximize reliability..." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">System Function (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('systemFunction', e.target.value)} placeholder="Provide torque..." />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Input Signals (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('inputSignals', e.target.value)} placeholder="Voltage, Current..." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Control Factors (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('controlFactors', e.target.value)} placeholder="Material type..." />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Expected Output (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('expectedOutput', e.target.value)} placeholder="Motion..." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Unexpected Output (comma sep)</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" onChange={(e) => handleArrayChange('unexpectedOutput', e.target.value)} placeholder="Heat, Noise..." />
                            </div>
                        </div>

                        <div className="border-t border-border pt-4">
                            <h3 className="font-semibold mb-2">Noise Factors (Comma Separated)</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <input className="bg-background border border-border rounded p-2" placeholder="Piece to Piece" onChange={(e) => handleNoiseFactorChange('pieceToPiece', e.target.value)} />
                                <input className="bg-background border border-border rounded p-2" placeholder="Change Over Time" onChange={(e) => handleNoiseFactorChange('changeOverTime', e.target.value)} />
                                <input className="bg-background border border-border rounded p-2" placeholder="Customer Usage" onChange={(e) => handleNoiseFactorChange('customerUsage', e.target.value)} />
                                <input className="bg-background border border-border rounded p-2" placeholder="External Environment" onChange={(e) => handleNoiseFactorChange('externalEnvironment', e.target.value)} />
                                <input className="bg-background border border-border rounded p-2" placeholder="Internal" onChange={(e) => handleNoiseFactorChange('internal', e.target.value)} />
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 mt-6">
                            <button type="button" onClick={onClose} className="bg-background px-4 py-2 rounded">Cancel</button>
                            <button type="submit" className="bg-primary text-white px-4 py-2 rounded">Create Draft</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const ApprovalModal: React.FC<{ isOpen: boolean; onClose: () => void; onApprove: (data: ApprovalData) => void }> = ({ isOpen, onClose, onApprove }) => {
    const [initials, setInitials] = useState('');
    const [note, setNote] = useState('');

    if (!isOpen) return null;

    const handleApprove = () => {
        if (!initials) {
            alert("Please enter your initials.");
            return;
        }
        onApprove({
            initials,
            note,
            timestamp: new Date().toISOString()
        });
        setInitials('');
        setNote('');
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">Approve P-Diagram</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Initials (Signature)</label>
                        <input
                            type="text"
                            value={initials}
                            onChange={(e) => setInitials(e.target.value)}
                            className="w-full bg-background border border-border rounded p-2"
                            placeholder="e.g. JD"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Approval Note (Optional)</label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="w-full bg-background border border-border rounded p-2 h-24"
                            placeholder="Notes on approval..."
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                    <button onClick={onClose} className="flex items-center gap-2 bg-background px-4 py-2 rounded border border-border hover:bg-gray-100">
                        <X size={16} /> Cancel
                    </button>
                    <button onClick={handleApprove} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        <Check size={16} /> Approve
                    </button>
                </div>
            </div>
        </div>
    );
}

const PDiagramPage: React.FC = () => {
    const { allData, masterFunctions, addPDiagram, deletePDiagram, activeProject } = useAppContext();
    const [viewMode, setViewMode] = useState<'active' | 'draft' | 'inactive' | 'generate'>('active');
    const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
    const [inactiveInitials, setInactiveInitials] = useState('');
    const [inactiveReason, setInactiveReason] = useState('');

    // Helper: Is item in active project?
    const isItemInProject = (diagram: PDiagramData) => {
        if (activeProject === 'all') return true;
        const activeProjectNormalized = normalizeProjectName(activeProject);
        const diagramProjectNormalized = normalizeProjectName(diagram.projectName || '');

        // 1. Direct Project check (Metadata Standards v2.0.0)
        if (diagramProjectNormalized) {
            return diagramProjectNormalized === activeProjectNormalized;
        }

        // 2. Fallback: Find the epoch this item belongs to
        if (diagram.epochId) {
            const epoch = allData.iterationHistory?.find(e =>
                (e.epochNumber || e.iteration) === diagram.epochId
            );
            if (epoch) {
                const projectName = extractProjectName(epoch.epochName || '');
                return normalizeProjectName(projectName) === activeProjectNormalized;
            }
        }

        // 3. Legacy Fallback: If no project/epoch metadata, assume it belongs to the seed project
        // This handles cases where legacy data was created before metadata standards
        if (!diagram.projectName && !diagram.epochId) {
            return activeProjectNormalized === 'aerator pro';  // Legacy seed data
        }

        return false;
    };

    // Extract unique Projects for metadata assignment
    const localProjects = useMemo(() => {
        const projectMap = new Map<string, Set<number>>();
        if (allData.iterationHistory) {
            allData.iterationHistory.forEach((ep) => {
                let projectName = "Other";
                if (ep.epochName) {
                    if (ep.epochName.includes(' - ')) {
                        projectName = ep.epochName.split(' - ')[0].trim();
                    } else if (ep.epochName.startsWith('Magnus Genesis')) {
                        projectName = "Magnus Genesis";
                    } else {
                        projectName = ep.epochName;
                    }
                }
                if (!projectMap.has(projectName)) projectMap.set(projectName, new Set());
                projectMap.get(projectName)!.add(ep.epochNumber || ep.iteration || 0);
            });
        }
        return Array.from(projectMap.entries()).map(([name, epochs]) => ({
            name,
            epochIds: Array.from(epochs).sort((a, b) => a - b)
        }));
    }, [allData.iterationHistory]);

    // NEW: Compute all available functions including custom ones from DB
    const filteredDiagrams = useMemo(() => {
        return allData.pDiagramList.filter(d => {
            // Project Filter
            if (!isItemInProject(d)) return false;

            // Status Filter - Default to 'active' for legacy data without status field
            const diagramStatus = d.status || 'active';
            return diagramStatus === viewMode;
        });
    }, [allData.pDiagramList, viewMode, activeProject, allData.iterationHistory]);

    const allFunctions = useMemo(() => {
        return filteredDiagrams.map(d => d.consolidatedFunctionId);
    }, [filteredDiagrams]);

    const [selectedFunction, setSelectedFunction] = useState<string>('');
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isApprovalModalOpen, setApprovalModalOpen] = useState(false);

    // Update selected function when view mode changes or list changes
    React.useEffect(() => {
        if (allFunctions.length > 0) {
            if (!allFunctions.includes(selectedFunction)) {
                setSelectedFunction(allFunctions[0]);
            }
        } else {
            setSelectedFunction('');
        }
    }, [allFunctions, viewMode]);


    const handleCreate = async (newData: Partial<PDiagramData>) => {
        if (!newData.consolidatedFunctionId) return;

        let epochId: number | undefined;
        let projectName: string | undefined;

        if (activeProject !== 'all') {
            projectName = activeProject;
            const normalizedActive = normalizeProjectName(activeProject);
            const proj = localProjects.find(p => normalizeProjectName(p.name) === normalizedActive);
            if (proj && proj.epochIds.length > 0) {
                epochId = proj.epochIds[proj.epochIds.length - 1];
            }
        }

        const completeData: PDiagramData = {
            systemName: `${newData.consolidatedFunctionId} P-Diagram`,
            consolidatedFunctionId: newData.consolidatedFunctionId,
            goal: newData.goal || [],
            inputSignals: newData.inputSignals || [],
            controlFactors: newData.controlFactors || [],
            systemFunction: newData.systemFunction || [],
            expectedOutput: newData.expectedOutput || [],
            unexpectedOutput: newData.unexpectedOutput || [],
            noiseFactors: newData.noiseFactors || {
                pieceToPiece: [],
                changeOverTime: [],
                customerUsage: [],
                externalEnvironment: [],
                internal: []
            },
            status: 'draft', // FORCE DRAFT STATUS ON CREATE
            projectName,
            epochId
        };

        try {
            await addPDiagram(completeData);
            setViewMode('draft');
            setSelectedFunction(completeData.consolidatedFunctionId);
        } catch (error) {
            alert("Failed to create P-Diagram. Please try again.");
            console.error(error);
        }
    };

    const handleApproval = async (approvalData: ApprovalData) => {
        const diagram = filteredDiagrams.find(p => p.consolidatedFunctionId === selectedFunction);
        if (!diagram) return;

        const updatedDiagram: PDiagramData = {
            ...diagram,
            status: 'active',
            approval: approvalData
        };

        try {
            // 1. Save as Active (this updates the main document ID)
            await addPDiagram(updatedDiagram);

            // 2. Delete the Draft (this removes the draft document ID)
            await deletePDiagram(diagram.consolidatedFunctionId, 'draft');

            setApprovalModalOpen(false);
            setViewMode('active');
            // Selected function stays the same, just moves view
        } catch (error) {
            console.error("Failed to approve diagram", error);
            alert("Failed to approve diagram.");
        }
    }

    const handleMarkInactive = async () => {
        if (!selectedFunction || !inactiveInitials.trim() || !inactiveReason.trim()) {
            alert('Please provide your initials and a reason for inactivation.');
            return;
        }

        const diagram = filteredDiagrams.find(d => d.consolidatedFunctionId === selectedFunction);
        if (!diagram) return;

        try {
            const inactivatedDiagram: PDiagramData = {
                ...diagram,
                status: 'inactive',
                inactivation: {
                    timestamp: new Date().toISOString(),
                    initials: inactiveInitials.trim(),
                    reason: inactiveReason.trim()
                }
            };

            await addPDiagram(inactivatedDiagram);
            setInactiveModalOpen(false);
            setInactiveInitials('');
            setInactiveReason('');
            alert('P-Diagram marked as inactive successfully!');
        } catch (error) {
            console.error('Failed to mark P-Diagram as inactive:', error);
            alert('Error marking P-Diagram as inactive. Please check console.');
        }
    };

    const diagramData = filteredDiagrams.find(p => p.consolidatedFunctionId === selectedFunction);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">Parameter Diagram (P-Diagram)</h1>
                    <p className="text-text-secondary mt-1">Select a Master Function to see its detailed diagram.</p>
                </div>

                <div className="flex items-center gap-4">
                    <ActiveProjectBadge showStandard={false} />
                    <ModuleInfoModal
                        title="Parameter Diagram (P-Diagram)"
                        description="Visualize the system's input/output relationship, including control factors, noise factors, and error states."
                        benefits={[
                            "Identify all noise factors affecting performance.",
                            "Define clear control factors for robust design.",
                            "Draft and approve P-Diagrams for different system functions."
                        ]}
                        usageInstructions={[
                            "Select a 'Master Function' to view its diagram.",
                            "Toggle between 'Active' and 'Draft' views.",
                            "Approve drafts to make them the active standard."
                        ]}
                    />
                    {/* View Switcher */}
                    <div className="flex bg-card border border-border rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('active')}
                            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${viewMode === 'active' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-background'
                                }`}
                        >
                            Active
                        </button>
                        <button
                            onClick={() => setViewMode('draft')}
                            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${viewMode === 'draft' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-background'
                                }`}
                        >
                            Drafts
                        </button>
                        <button
                            onClick={() => setViewMode('inactive')}
                            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${viewMode === 'inactive' ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:bg-background'}`}
                        >
                            Inactive
                        </button>
                        <button
                            onClick={() => setViewMode('generate')}
                            className={`px-3 py-1.5 text-sm font-medium rounded flex items-center gap-1 transition-colors ${viewMode === 'generate' ? 'bg-primary text-white shadow-sm' : 'text-primary hover:bg-primary/10'}`}
                        >
                            <Sparkles size={14} /> AI Draft
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* New Diagram Button REMOVED as requested. 
                           It was causing confusion when all functions already had diagrams.
                           Creation logic is still accessible via the empty state if needed, or we can restore it conditionally.
                           For now, removing it from the main header.
                        */}

                        <label htmlFor="p-select" className="text-sm font-medium">Function:</label>
                        <select
                            id="p-select"
                            value={selectedFunction}
                            onChange={(e) => setSelectedFunction(e.target.value)}
                            className="bg-card border border-border rounded-md px-3 py-1.5 text-sm w-64"
                            disabled={allFunctions.length === 0}
                        >
                            {allFunctions.length > 0 ? (
                                allFunctions.map(fn => (
                                    <option key={fn} value={fn}>{fn}</option>
                                ))
                            ) : (
                                <option>No {viewMode} diagrams</option>
                            )}
                        </select>
                    </div>
                </div>
            </div>

            {viewMode === 'generate' ? (
                <div className="bg-card border border-border rounded-lg p-4 pb-12 transition-all duration-300">
                    <PDiagramGenerator onGenerateDone={() => setViewMode('draft')} />
                </div>
            ) : diagramData ? (
                <div
                    key={selectedFunction}
                    className="bg-card border border-border rounded-lg p-4 space-y-4 transition-all duration-300 relative"
                    style={{ minHeight: '680px' }}
                >
                    {/* Approval Header for Drafts */}
                    {viewMode === 'draft' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-2 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-yellow-800">
                                <AlertTriangle size={18} />
                                <span className="font-medium">This diagram is a Draft.</span>
                            </div>
                            <button
                                onClick={() => setApprovalModalOpen(true)}
                                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-green-700 flex items-center gap-2"
                            >
                                <Check size={16} /> Approve & Release
                            </button>
                        </div>
                    )}

                    {/* Approval Info for Active */}
                    {viewMode === 'active' && diagramData.approval && (
                        <div className="bg-green-50 border border-green-200 rounded p-2 mb-2 flex justify-between items-center text-xs text-green-800">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={14} />
                                <span>Approved by <strong>{diagramData.approval.initials}</strong> on {new Date(diagramData.approval.timestamp).toLocaleDateString()}</span>
                            </div>
                            {diagramData.approval.note && <span className="italic">&quot;{diagramData.approval.note}&quot;</span>}
                            <button
                                onClick={() => setInactiveModalOpen(true)}
                                className="bg-red-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-red-700 flex items-center gap-1"
                            >
                                <XCircle size={14} /> Mark Inactive
                            </button>
                        </div>
                    )}

                    {/* Inactivation Info */}
                    {viewMode === 'inactive' && diagramData.inactivation && (
                        <div className="bg-gray-50 border border-gray-300 rounded p-2 mb-2 text-xs text-gray-700">
                            <div className="flex items-center gap-2">
                                <XCircle size={14} />
                                <span>Inactivated by <strong>{diagramData.inactivation.initials}</strong> on {new Date(diagramData.inactivation.timestamp).toLocaleDateString()}</span>
                            </div>
                            {diagramData.inactivation.reason && <p className="mt-1 italic">Reason: &quot;{diagramData.inactivation.reason}&quot;</p>}
                        </div>
                    )}

                    <div className="flex flex-col gap-4 h-full">
                        <div className="flex-grow flex flex-col lg:flex-row gap-4">
                            {/* Left Column */}
                            <div className="flex flex-col gap-4 flex-1">
                                <PDiagramBox title="Goal" items={diagramData.goal} icon={<Target size={16} />} className="bg-primary/10" />
                                <PDiagramBox title="Control Factors" items={diagramData.controlFactors} icon={<SlidersHorizontal size={16} />} />
                            </div>

                            {/* Center Column */}
                            <div className="flex flex-col gap-4 flex-1 lg:flex-[2]">
                                <PDiagramBox title="Input Signals" items={diagramData.inputSignals} icon={<LogIn size={16} />} />
                                <div className="flex-grow">
                                    <PDiagramBox title="System Function" items={diagramData.systemFunction} icon={<Settings size={16} />} className="bg-primary/10" contentClassName="text-base" />
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="flex flex-col gap-4 flex-1">
                                <PDiagramBox title="Expected Output" items={diagramData.expectedOutput} icon={<CheckCircle2 size={16} />} />
                                <PDiagramBox title="Unexpected Output" items={diagramData.unexpectedOutput} icon={<AlertTriangle size={16} />} />
                            </div>
                        </div>

                        {/* Noise Factors at bottom */}
                        <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded p-4">
                            <h3 className="font-bold text-center text-primary mb-3 text-lg flex items-center justify-center gap-2">
                                <Waves size={20} />Noise Factors
                            </h3>
                            <NoiseGrid noiseFactors={diagramData.noiseFactors} />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center h-64 bg-card border border-border rounded-lg text-text-secondary">
                    <p>No {viewMode} P-Diagram found.</p>
                    {viewMode === 'draft' && (
                        <button
                            onClick={() => setCreateModalOpen(true)}
                            className="mt-4 text-primary hover:underline"
                        >
                            Create new draft
                        </button>
                    )}
                </div>
            )}

            <CreatePDiagramModal
                isOpen={isCreateModalOpen}
                onClose={() => setCreateModalOpen(false)}
                currentDiagrams={allData.pDiagramList}
                masterFunctions={masterFunctions}
                onCreate={handleCreate}
            />

            <ApprovalModal
                isOpen={isApprovalModalOpen}
                onClose={() => setApprovalModalOpen(false)}
                onApprove={handleApproval}
            />

            {/* Inactivation Modal */}
            {inactiveModalOpen && selectedFunction && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4 text-red-600">Mark P-Diagram as Inactive</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="inactive-initials" className="block text-sm font-medium mb-1">Your Initials</label>
                                <input id="inactive-initials" type="text" value={inactiveInitials} onChange={(e) => setInactiveInitials(e.target.value)} className="w-full bg-background p-2 rounded border border-border" placeholder="e.g. J.D." />
                            </div>
                            <div>
                                <label htmlFor="inactive-reason" className="block text-sm font-medium mb-1">Reason for Inactivation</label>
                                <textarea id="inactive-reason" value={inactiveReason} onChange={(e) => setInactiveReason(e.target.value)} className="w-full bg-background p-2 rounded border border-border h-24" placeholder="Why is this diagram being inactivated?" required />
                            </div>
                            <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">This will timestamp and log the inactivation. The diagram will be moved to the Inactive view.</p>
                        </div>
                        <div className="flex justify-end gap-4 mt-6">
                            <button onClick={() => { setInactiveModalOpen(false); setInactiveInitials(''); setInactiveReason(''); }} className="bg-background px-4 py-2 rounded hover:bg-gray-100">Cancel</button>
                            <button onClick={handleMarkInactive} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">Mark Inactive</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PDiagramPage;
